'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function StairCalculator() {
  const t = useTranslations("tools.stairCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [mode, setMode] = useState<'run' | 'totalrun'>('run');
  const [riseMode, setRiseMode] = useState<'auto' | 'fixed' | 'steps'>('auto');
  const [stringerType, setStringerType] = useState<'standard' | 'flush'>('standard');

  const [inputs, setInputs] = useState({
    run: 10,
    runUnit: 'in' as 'ft' | 'in' | 'm' | 'cm',
    totalRun: 120,
    totalRunUnit: 'ft' as 'ft' | 'in' | 'm' | 'cm',
    totalRise: 120,
    totalRiseUnit: 'in' as 'ft' | 'in' | 'm' | 'cm',
    riseValue: 7,
    riseUnit: 'in' as 'ft' | 'in' | 'm' | 'cm',
    stepsValue: 15,
  });

  const [results, setResults] = useState<{
    numSteps: number;
    risePerStep: number;
    runPerStep: number;
    totalRun: number;
    stringerLength: number;
    angle: number;
    compliant: boolean;
    warnings: string[];
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const convertToInches = (value: number, unit: 'ft' | 'in' | 'm' | 'cm'): number => {
    const conversions = {
      'in': 1,
      'ft': 12,
      'cm': 0.393701,
      'm': 39.3701
    };
    return value * conversions[unit];
  };

  const formatResult = (inches: number, targetUnit: 'ft' | 'in' | 'm' | 'cm'): string => {
    const conversions = {
      'in': { factor: 1, label: 'in' },
      'ft': { factor: 1/12, label: 'ft' },
      'cm': { factor: 2.54, label: 'cm' },
      'm': { factor: 0.0254, label: 'm' }
    };

    const conv = conversions[targetUnit];
    const value = (inches * conv.factor).toFixed(2);
    return `${value} ${conv.label}`;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const totalRiseInches = convertToInches(inputs.totalRise, inputs.totalRiseUnit);

    let numSteps: number;
    let risePerStep: number;
    let runPerStep: number;
    let totalRunInches: number;

    if (mode === 'run') {
      runPerStep = convertToInches(inputs.run, inputs.runUnit);

      if (riseMode === 'auto') {
        numSteps = Math.round(totalRiseInches / 7);
        if (numSteps < 1) numSteps = 1;
      } else if (riseMode === 'fixed') {
        const fixedRise = convertToInches(inputs.riseValue, inputs.riseUnit);
        numSteps = Math.ceil(totalRiseInches / fixedRise);
      } else {
        numSteps = inputs.stepsValue;
      }

      risePerStep = totalRiseInches / numSteps;
      totalRunInches = runPerStep * numSteps;

    } else {
      totalRunInches = convertToInches(inputs.totalRun, inputs.totalRunUnit);

      if (riseMode === 'auto') {
        numSteps = Math.round(totalRiseInches / 7);
        if (numSteps < 1) numSteps = 1;
      } else if (riseMode === 'fixed') {
        const fixedRise = convertToInches(inputs.riseValue, inputs.riseUnit);
        numSteps = Math.ceil(totalRiseInches / fixedRise);
      } else {
        numSteps = inputs.stepsValue;
      }

      risePerStep = totalRiseInches / numSteps;
      runPerStep = totalRunInches / numSteps;
    }

    const stringerLength = Math.sqrt(Math.pow(totalRiseInches, 2) + Math.pow(totalRunInches, 2));
    const angleRad = Math.atan(totalRiseInches / totalRunInches);
    const angleDeg = angleRad * (180 / Math.PI);

    const warnings: string[] = [];
    let compliant = true;

    if (risePerStep < 4 || risePerStep > 7.75) {
      warnings.push(t('warningRise'));
      compliant = false;
    }
    if (runPerStep < 10) {
      warnings.push(t('warningRun'));
      compliant = false;
    }

    setResults({
      numSteps,
      risePerStep,
      runPerStep,
      totalRun: totalRunInches,
      stringerLength,
      angle: angleDeg,
      compliant,
      warnings
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem', marginBottom: '2rem' },
    { padding: '2.5rem', borderRadius: '1.5rem', marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textSecondary, marginBottom: '0.75rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem', marginBottom: '1rem' },
    { fontSize: '1.375rem', marginBottom: '1rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const modeTabsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    background: '#f1f5f9',
    padding: '0.25rem',
    borderRadius: '0.5rem',
  };

  const modeTabStyle = (active: boolean): React.CSSProperties => getResponsiveStyle(
    {
      flex: 1,
      background: active ? 'white' : 'transparent',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      fontWeight: 500,
      color: active ? '#2563eb' : '#64748b',
      boxShadow: active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
      transition: 'all 0.2s',
    },
    { padding: '0.625rem 0.75rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1rem', fontSize: '0.9375rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'border-color 0.2s',
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1.0625rem' },
    { padding: '0.875rem 1rem', fontSize: '1.125rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'border-color 0.2s',
      background: 'white',
    },
    { padding: '0.75rem', fontSize: '1rem', width: '100px' },
    { padding: '0.875rem 1rem', fontSize: '1.0625rem', width: '110px' },
    { padding: '0.875rem 1rem', fontSize: '1.125rem', width: '120px' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '1.5rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.0625rem', borderRadius: '0.625rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2rem', borderRadius: '1.25rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultItemStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #2563eb',
  };

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    color: '#64748b',
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
    fontWeight: 500,
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const warningBoxStyle: React.CSSProperties = {
    background: '#fef3c7',
    borderLeft: '4px solid #f59e0b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
  };

  const complianceBoxStyle: React.CSSProperties = {
    background: '#dcfce7',
    borderLeft: '4px solid #16a34a',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
  };

  const diagramContainerStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    { padding: '1.5rem', minHeight: '250px' },
    { padding: '2rem', minHeight: '300px' },
    { padding: '2rem', minHeight: '350px' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <h2 style={{ ...h2Style, textAlign: 'center' as const, marginBottom: '2rem' }}>
            {t("toolTitle")}
          </h2>

          <div style={calculatorLayoutStyle}>
            {/* Calculator Form */}
            <div style={glassCardStyle}>
              <div style={modeTabsStyle}>
                <button
                  type="button"
                  style={modeTabStyle(mode === 'run')}
                  onClick={() => setMode('run')}
                >
                  {t("modeRun")}
                </button>
                <button
                  type="button"
                  style={modeTabStyle(mode === 'totalrun')}
                  onClick={() => setMode('totalrun')}
                >
                  {t("modeTotalRun")}
                </button>
              </div>

              <form onSubmit={handleCalculate}>
                {/* Run or Total Run Input */}
                {mode === 'run' ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textPrimary, fontWeight: 500, fontSize: '0.9rem' }}>
                      {t("runLabel")}
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={inputs.run}
                        onChange={(e) => setInputs({...inputs, run: parseFloat(e.target.value) || 0})}
                        style={inputStyle}
                      />
                      <select
                        value={inputs.runUnit}
                        onChange={(e) => setInputs({...inputs, runUnit: e.target.value as any})}
                        style={selectStyle}
                      >
                        <option value="ft">{t("unitFeet")}</option>
                        <option value="in">{t("unitInches")}</option>
                        <option value="m">{t("unitMeters")}</option>
                        <option value="cm">{t("unitCentimeters")}</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textPrimary, fontWeight: 500, fontSize: '0.9rem' }}>
                      {t("totalRunLabel")}
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={inputs.totalRun}
                        onChange={(e) => setInputs({...inputs, totalRun: parseFloat(e.target.value) || 0})}
                        style={inputStyle}
                      />
                      <select
                        value={inputs.totalRunUnit}
                        onChange={(e) => setInputs({...inputs, totalRunUnit: e.target.value as any})}
                        style={selectStyle}
                      >
                        <option value="ft">{t("unitFeet")}</option>
                        <option value="in">{t("unitInches")}</option>
                        <option value="m">{t("unitMeters")}</option>
                        <option value="cm">{t("unitCentimeters")}</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Total Rise Input */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textPrimary, fontWeight: 500, fontSize: '0.9rem' }}>
                    {t("totalRiseLabel")}
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={inputs.totalRise}
                      onChange={(e) => setInputs({...inputs, totalRise: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                    />
                    <select
                      value={inputs.totalRiseUnit}
                      onChange={(e) => setInputs({...inputs, totalRiseUnit: e.target.value as any})}
                      style={selectStyle}
                    >
                      <option value="ft">{t("unitFeet")}</option>
                      <option value="in">{t("unitInches")}</option>
                      <option value="m">{t("unitMeters")}</option>
                      <option value="cm">{t("unitCentimeters")}</option>
                    </select>
                  </div>
                </div>

                {/* Rise Options */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={h3Style}>{t("riseOptionsTitle")}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="auto-rise"
                        name="rise-mode"
                        checked={riseMode === 'auto'}
                        onChange={() => setRiseMode('auto')}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label htmlFor="auto-rise" style={{ margin: 0, cursor: 'pointer', fontSize: '0.9rem' }}>
                        {t("riseAuto")}
                      </label>
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          id="fixed-rise"
                          name="rise-mode"
                          checked={riseMode === 'fixed'}
                          onChange={() => setRiseMode('fixed')}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label htmlFor="fixed-rise" style={{ margin: 0, cursor: 'pointer', fontSize: '0.9rem' }}>
                          {t("riseFixed")}
                        </label>
                      </div>
                      {riseMode === 'fixed' && (
                        <div style={{ marginTop: '0.75rem', paddingLeft: '1.75rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={inputs.riseValue}
                              onChange={(e) => setInputs({...inputs, riseValue: parseFloat(e.target.value) || 0})}
                              style={inputStyle}
                            />
                            <select
                              value={inputs.riseUnit}
                              onChange={(e) => setInputs({...inputs, riseUnit: e.target.value as any})}
                              style={selectStyle}
                            >
                              <option value="ft">{t("unitFeet")}</option>
                              <option value="in">{t("unitInches")}</option>
                              <option value="m">{t("unitMeters")}</option>
                              <option value="cm">{t("unitCentimeters")}</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          id="fixed-steps"
                          name="rise-mode"
                          checked={riseMode === 'steps'}
                          onChange={() => setRiseMode('steps')}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label htmlFor="fixed-steps" style={{ margin: 0, cursor: 'pointer', fontSize: '0.9rem' }}>
                          {t("riseSteps")}
                        </label>
                      </div>
                      {riseMode === 'steps' && (
                        <div style={{ marginTop: '0.75rem', paddingLeft: '1.75rem' }}>
                          <input
                            type="number"
                            min="1"
                            value={inputs.stepsValue}
                            onChange={(e) => setInputs({...inputs, stepsValue: parseInt(e.target.value) || 1})}
                            style={inputStyle}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stringer Type */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={h3Style}>{t("stringerTypeTitle")}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="standard-stringer"
                        name="stringer-type"
                        checked={stringerType === 'standard'}
                        onChange={() => setStringerType('standard')}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label htmlFor="standard-stringer" style={{ margin: 0, cursor: 'pointer', fontSize: '0.9rem' }}>
                        {t("stringerStandard")}
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        id="flush-stringer"
                        name="stringer-type"
                        checked={stringerType === 'flush'}
                        onChange={() => setStringerType('flush')}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label htmlFor="flush-stringer" style={{ margin: 0, cursor: 'pointer', fontSize: '0.9rem' }}>
                        {t("stringerFlush")}
                      </label>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  style={calculateButtonStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {/* Stair Diagram */}
            <div style={diagramContainerStyle}>
              <svg viewBox="0 0 400 350" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: '400px', height: 'auto' }}>
                <defs>
                  <linearGradient id="stairGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#2563eb', stopOpacity: 0.1 }} />
                    <stop offset="100%" style={{ stopColor: '#7c3aed', stopOpacity: 0.1 }} />
                  </linearGradient>
                </defs>

                <rect x="50" y="50" width="300" height="250" fill="url(#stairGrad)" stroke="#cbd5e1" strokeWidth="2" rx="8"/>

                <line x1="80" y1="250" x2="320" y2="250" stroke="#2563eb" strokeWidth="2"/>
                <line x1="80" y1="250" x2="80" y2="100" stroke="#2563eb" strokeWidth="2"/>

                <path d="M 80 250 L 140 250 L 140 210 L 200 210 L 200 170 L 260 170 L 260 130 L 320 130"
                      fill="none" stroke="#7c3aed" strokeWidth="3"/>

                <rect x="80" y="250" width="60" height="5" fill="#f59e0b" opacity="0.7"/>
                <rect x="140" y="210" width="60" height="5" fill="#f59e0b" opacity="0.7"/>
                <rect x="200" y="170" width="60" height="5" fill="#f59e0b" opacity="0.7"/>
                <rect x="260" y="130" width="60" height="5" fill="#f59e0b" opacity="0.7"/>

                <line x1="140" y1="255" x2="140" y2="210" stroke="#64748b" strokeWidth="1" strokeDasharray="3,3"/>
                <text x="145" y="235" fontSize="12" fill="#64748b">{t("diagramRise")}</text>

                <line x1="80" y1="255" x2="140" y2="255" stroke="#64748b" strokeWidth="1" strokeDasharray="3,3"/>
                <text x="95" y="270" fontSize="12" fill="#64748b">{t("diagramRun")}</text>

                <line x1="75" y1="250" x2="75" y2="100" stroke="#16a34a" strokeWidth="2"/>
                <text x="30" y="180" fontSize="12" fill="#16a34a" fontWeight="bold">{t("diagramTotalRise")}</text>

                <line x1="80" y1="260" x2="320" y2="260" stroke="#16a34a" strokeWidth="2"/>
                <text x="170" y="285" fontSize="12" fill="#16a34a" fontWeight="bold">{t("diagramTotalRun")}</text>
              </svg>
            </div>
          </div>

          {/* Results */}
          {results && (
            <div style={resultsCardStyle}>
              <h3 style={{ ...h2Style, textAlign: 'center' as const, marginBottom: '1.5rem' }}>
                {t("resultsTitle")}
              </h3>
              <div style={resultsGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultNumSteps")}</span>
                  <span style={resultValueStyle}>{results.numSteps}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultRisePerStep")}</span>
                  <span style={resultValueStyle} dangerouslySetInnerHTML={{ __html: formatResult(results.risePerStep, inputs.totalRiseUnit) }} />
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultRunPerStep")}</span>
                  <span style={resultValueStyle} dangerouslySetInnerHTML={{ __html: formatResult(results.runPerStep, inputs.totalRiseUnit) }} />
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultTotalRun")}</span>
                  <span style={resultValueStyle} dangerouslySetInnerHTML={{ __html: formatResult(results.totalRun, inputs.totalRiseUnit) }} />
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultStringerLength")}</span>
                  <span style={resultValueStyle} dangerouslySetInnerHTML={{ __html: formatResult(results.stringerLength, inputs.totalRiseUnit) }} />
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("resultAngle")}</span>
                  <span style={resultValueStyle}>{results.angle.toFixed(1)}°</span>
                </div>
              </div>

              {results.compliant ? (
                <div style={complianceBoxStyle}>
                  <p style={{ margin: 0, color: '#14532d', fontSize: '0.9rem' }}>
                    ✓ {t("compliancePass")}
                  </p>
                </div>
              ) : (
                <div style={warningBoxStyle}>
                  <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
                    ⚠️ {results.warnings.join('. ')}. {t("complianceVerify")}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep5")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <h3 style={h3Style}>{t("insightsSubtitle1")}</h3>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>{t("insightPoint1")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("insightPoint2")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("insightPoint3")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("insightPoint4")}</li>
        </ul>

        <h3 style={{ ...h3Style, marginTop: '1.5rem' }}>{t("insightsSubtitle2")}</h3>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>{t("bestPractice1")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("bestPractice2")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("bestPractice3")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("bestPractice4")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("bestPractice5")}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph4")}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion1")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion2")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion3")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion4")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer4")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion5")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer5")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://stair-calc.com" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference1")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.calculator.net/stair-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference2")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.omnicalculator.com/construction/stairs" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference3")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.homedepot.com/c/ah/how-to-measure-stair-stringers/9ba683603be9fa5395fab90122fef874" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t("reference4")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}